import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import SearchBar from "@/components/SearchBar";
import ListingCard from "@/components/ListingCard";
import { Empty } from "@/components/common";
import { useLang } from "@/lib/i18n";
import { api, eur } from "@/lib/api";

const TYPES = ["apartment", "studio", "loft", "house", "room", "villa"];
export const AMENITIES = ["wifi", "parking", "desk", "ac", "washer", "kitchen", "elevator", "tv", "heating", "balcony"];

function Filters({ value, onChange, onApply }) {
  const { t } = useLang();
  const toggle = (k, v) => {
    const arr = value[k] || [];
    onChange({ ...value, [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v] });
  };
  return (
    <div className="space-y-7" data-testid="filters-panel">
      <div>
        <p className="text-sm font-bold text-[#142B5C]">{t("price_range")}</p>
        <Slider className="mt-5" min={0} max={5000} step={50} value={value.price} onValueChange={(v) => onChange({ ...value, price: v })} data-testid="filter-price-slider" />
        <p className="mt-3 text-sm text-slate-600" data-testid="filter-price-label">{eur(value.price[0])} – {eur(value.price[1])}{value.price[1] >= 5000 ? "+" : ""}</p>
      </div>
      <div>
        <p className="text-sm font-bold text-[#142B5C] mb-3">{t("type")}</p>
        <div className="grid grid-cols-2 gap-2">
          {TYPES.map((ty) => (
            <label key={ty} className="flex items-center gap-2 text-sm min-h-[40px]"><Checkbox checked={value.types.includes(ty)} onCheckedChange={() => toggle("types", ty)} data-testid={`filter-type-${ty}`} />{t(`t_${ty}`)}</label>
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm font-bold text-[#142B5C] mb-3">{t("min_bedrooms")}</p>
        <div className="flex gap-2">
          {[0, 1, 2, 3, 4].map((n) => (
            <button key={n} type="button" onClick={() => onChange({ ...value, bedrooms: n })} data-testid={`filter-bedrooms-${n}`}
              className={`h-11 min-w-[44px] rounded-xl border px-3 text-sm font-bold transition-colors ${value.bedrooms === n ? "bg-[#142B5C] text-white border-[#142B5C]" : "bg-white border-slate-200 text-slate-600"}`}>
              {n === 0 ? t("any") : `${n}+`}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-sm font-bold text-[#142B5C] mb-3">{t("amenities")}</p>
        <div className="grid gap-1">
          {AMENITIES.map((a) => (
            <label key={a} className="flex items-center gap-2 text-sm min-h-[40px]"><Checkbox checked={value.amenities.includes(a)} onCheckedChange={() => toggle("amenities", a)} data-testid={`filter-amenity-${a}`} />{t(`a_${a}`)}</label>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={() => onApply({ price: [0, 5000], types: [], bedrooms: 0, amenities: [] })} className="h-12 flex-1 rounded-full border border-slate-300 font-semibold" data-testid="filters-reset-btn">{t("reset")}</button>
        <button type="button" onClick={() => onApply(value)} className="h-12 flex-1 rounded-full bg-[#FF6B00] font-bold text-white hover:bg-[#E05A00]" data-testid="filters-apply-btn">{t("apply")}</button>
      </div>
    </div>
  );
}

const fromParams = (sp) => ({
  price: [Number(sp.get("min_price") || 0), Number(sp.get("max_price") || 5000)],
  types: sp.get("property_type")?.split(",").filter(Boolean) || [],
  bedrooms: Number(sp.get("bedrooms") || 0),
  amenities: sp.get("amenities")?.split(",").filter(Boolean) || [],
});

export default function Search() {
  const { t } = useLang();
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState(fromParams(sp));
  const [open, setOpen] = useState(false);
  const sort = sp.get("sort") || "newest";

  useEffect(() => {
    const params = Object.fromEntries(sp.entries());
    setItems(null);
    api.get("/listings", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [sp]);

  const apply = (v) => {
    const n = new URLSearchParams(sp);
    const setOr = (k, val) => (val ? n.set(k, val) : n.delete(k));
    setOr("min_price", v.price[0] > 0 ? v.price[0] : "");
    setOr("max_price", v.price[1] < 5000 ? v.price[1] : "");
    setOr("property_type", v.types.join(","));
    setOr("bedrooms", v.bedrooms || "");
    setOr("amenities", v.amenities.join(","));
    setDraft(v);
    setSp(n);
    setOpen(false);
  };
  const setSort = (v) => { const n = new URLSearchParams(sp); n.set("sort", v); setSp(n); };
  const q = ["check_in", "check_out", "guests"].filter((k) => sp.get(k)).map((k) => `${k}=${sp.get(k)}`).join("&");

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="search-page">
      <div className="rounded-2xl bg-white border border-slate-200 p-3"><SearchBar compact initial={Object.fromEntries(sp.entries())} /></div>
      <div className="mt-6 flex items-center justify-between gap-3">
        <p className="font-display text-lg sm:text-xl font-bold text-[#0B192C]" data-testid="results-count">{items ? t("results", { n: items.length }) : t("loading")}</p>
        <div className="flex items-center gap-2">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden h-11 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold" data-testid="open-filters-btn"><SlidersHorizontal className="h-4 w-4" />{t("filters")}</button>
            </SheetTrigger>
            <SheetContent aria-describedby={undefined} side="right" className="w-[90vw] max-w-sm overflow-y-auto">
              <SheetTitle className="mb-6 font-display">{t("filters")}</SheetTitle>
              <Filters value={draft} onChange={setDraft} onApply={apply} />
            </SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-11 w-[170px] rounded-full bg-white" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["newest", "price_asc", "price_desc", "size"].map((s) => <SelectItem key={s} value={s} data-testid={`sort-${s}`}>{t(`sort_${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-6 grid gap-8 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block"><div className="sticky top-28 rounded-2xl bg-white border border-slate-200 p-6"><Filters value={draft} onChange={setDraft} onApply={apply} /></div></aside>
        <div>
          {items && items.length === 0 && <Empty text={`${t("no_results")} ${t("no_results_sub")}`} testid="no-results" />}
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="results-grid">
            {(items || []).map((l) => <ListingCard key={l.listing_id} l={l} query={q ? `?${q}` : ""} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
